/**
 * Socket Event Types
 * TypeScript interfaces for all WebSocket events
 * Matches backend event constants for type safety
 */

// CLIENT → SERVER Events
export const CLIENT_EVENTS = {
  SEND_MESSAGE: 'send_message',
  MARK_READ: 'mark_read',
  TOGGLE_REACTION: 'toggle_reaction',
  TYPING_START: 'typing_start',
  TYPING_STOP: 'typing_stop',
  JOIN_CONVERSATION: 'join_conversation',
  LEAVE_CONVERSATION: 'leave_conversation'
} as const

// SERVER → CLIENT Events
export const SERVER_EVENTS = {
  NEW_MESSAGE: 'new_message',
  MESSAGE_SENT: 'message_sent',
  MESSAGE_READ: 'message_read',
  MESSAGE_ERROR: 'message_error',
  REACTION_TOGGLED: 'reaction_toggled',
  CONVERSATION_UPDATED: 'conversation_updated',
  USER_TYPING: 'user_typing',
  USER_ONLINE: 'user_online',
  USER_OFFLINE: 'user_offline',
  AUTHENTICATED: 'authenticated',
  AUTH_ERROR: 'auth_error',
  PRODUCT_UNLOCKED: 'product_unlocked'
} as const

// Event Payload Types

export interface SendMessagePayload {
  conversation_id: string
  content: string
  attachments?: any[]
  tempId?: string // Frontend's optimistic ID
  reply_to_message_id?: string // Reply target message ID
  reply_to_gallery_image_id?: string // Gallery image reply target ID
}

export interface NewMessagePayload {
  conversation_id: string
  message: Message
  conversation?: Conversation
}

export interface MessageSentPayload {
  tempId?: string // Frontend's optimistic ID to replace
  message: Message
  // === PER-MESSAGE MOON COST ===
  moonDeduction?: {
    amountDeducted: number
    newBalance: number
  }
  // === END PER-MESSAGE MOON COST ===
}

export interface MessageReadPayload {
  conversation_id: string
  user_id: string
}

export interface MessageErrorPayload {
  error: string
  details?: string
  // === PER-MESSAGE MOON COST ===
  code?: string           // e.g., 'INSUFFICIENT_MOONS'
  balance?: number        // current moon balance
  required?: number       // moons needed
  tempId?: string         // for removing optimistic message
  // === END PER-MESSAGE MOON COST ===
}

export interface MarkReadPayload {
  conversation_id: string
}

export interface TypingStartPayload {
  conversation_id: string
}

export interface TypingStopPayload {
  conversation_id: string
}

export interface UserTypingPayload {
  conversation_id: string
  user: {
    id?: string
    username?: string
  }
}

export interface ToggleReactionPayload {
  message_id: string
  emoji: string
  conversation_id: string
}

export interface ReactionToggledPayload {
  message_id: string
  conversation_id: string
  action: 'added' | 'removed'
  reaction: Reaction | null
  user_id: string
}

export interface ConversationUpdatedPayload {
  conversation: Conversation
}

export interface ProductUnlockedPayload {
  product_id: string
  user_id: string
}

// Data Types (matching backend)

export interface Reaction {
  id: string
  message_id: string
  user_id: string
  conversation_id: string
  emoji: string
  created_at: string
  user?: {
    id?: string
    username: string | null
    avatar_url: string | null
  }
}

export interface Message {
  id: string
  conversation_id: string
  sender_id: string
  recipient_id: string
  content: string
  created_at: string
  read_at: string | null
  attachments?: any[]
  reactions?: Reaction[]
  reply_to_message_id?: string | null // ID of message being replied to
  reply_to?: { // Nested reply context loaded from backend
    id: string
    content: string
    created_at: string
    attachments?: any[]
    sender?: {
      id?: string
      username: string | null
      avatar_url: string | null
    }
  } | null
  // Gallery image reply support
  reply_to_gallery_image_id?: string | null // ID of gallery image being replied to
  reply_to_gallery_image?: { // Nested gallery image context loaded from backend
    id: string
    title: string | null
    thumbnail_url: string
    original_width?: number
    original_height?: number
  } | null
  sender?: {
    id?: string
    username: string | null
    avatar_url: string | null
  }
  recipient?: {
    id?: string
    username: string | null
    avatar_url: string | null
  }
  is_priority?: boolean // Whether this message is a priority message
}

export interface Conversation {
  id: string
  fan_id: string
  creator_id: string
  status: 'active' | 'archived' | 'flagged'
  unread_count: number
  last_message_at: string
  last_message_content: string | null
  created_at?: string
  updated_at?: string
  fan?: {
    id: string
    email: string
    username: string | null
    avatar_url: string | null
    first_name: string | null
    last_name: string | null
  }
  creator?: {
    id: string
    email: string
    username: string | null
    avatar_url: string | null
  }
}

// Socket Event Listener Types
export type NewMessageListener = (payload: NewMessagePayload) => void
export type MessageSentListener = (payload: MessageSentPayload) => void
export type MessageReadListener = (payload: MessageReadPayload) => void
export type MessageErrorListener = (payload: MessageErrorPayload) => void
export type ReactionToggledListener = (payload: ReactionToggledPayload) => void
export type ConversationUpdatedListener = (payload: ConversationUpdatedPayload) => void
export type UserTypingListener = (payload: UserTypingPayload) => void
export type ProductUnlockedListener = (payload: ProductUnlockedPayload) => void
